import type { FormEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from './feedback.js';
import { SURFACE_CARD_CLASS, useIsOnSurface } from './surface.js';

/**
 * Form layout and validation presentation.
 *
 * `Form` owns the submit plumbing so no page re-implements it: Enter submits,
 * the native browser validation bubble is suppressed in favour of the
 * translated messages, and a submit while one is already in flight is ignored.
 *
 * The summary is shown only AFTER a submit attempt, and only when something is
 * wrong. Marking fields red before anyone has tried anything treats a
 * half-filled form as a mistake, which it is not.
 */

export interface FormProps {
  readonly onSubmit: () => void | Promise<void>;
  readonly isSubmitting?: boolean;
  /** Number of field-level problems, used to decide the summary. */
  readonly errorCount?: number;
  readonly hasSubmitted?: boolean;
  readonly children: ReactNode;
  readonly actions?: ReactNode;
}

export function Form({
  onSubmit,
  isSubmitting = false,
  errorCount = 0,
  hasSubmitted = false,
  children,
  actions,
}: FormProps) {
  const { t } = useTranslation();

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitting) return;
    void onSubmit();
  };

  const showSummary = hasSubmitted && errorCount > 0;

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-gutter">
      {showSummary ? (
        <Alert
          tone="danger"
          title={t('validation.summaryTitle')}
          description={t('validation.summaryDescription', { count: errorCount })}
        />
      ) : null}

      {children}

      {actions ? <div className="pt-2">{actions}</div> : null}
    </form>
  );
}

/**
 * A titled group of related fields.
 *
 * A `fieldset` with a `legend`, not a styled div: it is what tells assistive
 * technology that these inputs belong together.
 */
export function FormSection({
  title,
  description,
  children,
  columns = 2,
  twoColumnsFrom = 'sm',
}: {
  /** Already translated. */
  readonly title: string;
  readonly description?: string;
  readonly children: ReactNode;
  readonly columns?: 1 | 2;
  /**
   * Where two columns start. `sm` for most forms; `lg` keeps a tablet on one
   * column where a form is filled standing at a counter (sample receiving).
   */
  readonly twoColumnsFrom?: 'sm' | 'lg';
}) {
  // On the page ground a section is a card; inside a panel or a dialog it is
  // already on one, and a second card inside the first would be noise.
  const isOnSurface = useIsOnSurface();
  const fieldset = (
    <fieldset className="min-w-0 border-0 p-0">
      <legend
        className={`text-section font-semibold text-default-900 ${
          description ? 'mb-1' : 'mb-stack'
        }`}
      >
        {title}
      </legend>
      {description ? (
        <p className="mb-stack text-body text-default-600">{description}</p>
      ) : null}
      <div
        className={`grid gap-x-gutter gap-y-stack ${
          // One column on a phone; the requested count from `sm` upwards. Field
          // widths are never fixed in pixels, so a long Arabic label wraps
          // rather than overflowing.
          columns === 1
            ? 'grid-cols-1'
            : twoColumnsFrom === 'lg'
              ? 'grid-cols-1 lg:grid-cols-2'
              : 'grid-cols-1 sm:grid-cols-2'
        }`}
      >
        {children}
      </div>
    </fieldset>
  );
  return isOnSurface ? (
    fieldset
  ) : (
    <section className={`min-w-0 ${SURFACE_CARD_CLASS}`}>{fieldset}</section>
  );
}

/**
 * A read-only fact.
 *
 * Used where a value is shown but not editable — a generated identifier, a
 * recorded timestamp. Visually distinct from an input on purpose: a value that
 * looks editable but is not is a small lie about what the page can do.
 */
export function ReadOnlyField({
  label,
  children,
}: {
  /** Already translated. */
  readonly label: string;
  readonly children: ReactNode;
}) {
  return (
    <div className="min-w-0">
      <p className="text-caption text-default-600">{label}</p>
      {/* A div, not a p: the value may be a status badge or a link list, and
          block content inside a paragraph is invalid markup. */}
      <div className="mt-1 break-words text-body text-default-800">{children}</div>
    </div>
  );
}
